import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useEnhancedGameSession } from '../hooks/useEnhancedGameSession'
import { UserProfileManager } from '../utils/userProfile'
import { GoFishAI } from '../utils/ai/gofish'
import { GameLobby } from '../components/GameLobby'

import { GameRoomHeader, GameRoomSidebar } from '../components/GameRoom'
import type { Message, HistoryItem } from '../components/GameRoom/GameRoomSidebar'
import { useSidebar } from '../hooks/useSidebar'
import * as PlayingCardDeck from '@letele/playing-cards'
import '../styles/GoFish.css'
import '../styles/UniversalGameRoom.css'
import '../styles/mobile-fixes.css'
import '../styles/mobile-bottom-sheet.css'

type Rank = 'A' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K'
type Suit = '♠' | '♥' | '♦' | '♣'
type Card = { rank: Rank; suit: Suit }

type GameMode = 'select' | 'local' | 'network' | 'ai'
type GamePhase = 'select' | 'setup' | 'lobby' | 'playing' | 'finished'
type AIDifficulty = 'easy' | 'medium' | 'hard'

type Notification = {
  message: string
  type: 'info' | 'success' | 'error'
}

type Player = {
  id: number
  name: string
  hand: Card[]
  books: Rank[]
}

const RANKS: Rank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K']
const SUITS: Suit[] = ['♠', '♥', '♦', '♣']

export default function GoFish() {
  const [searchParams] = useSearchParams()
  const joinCode = searchParams.get('join')

  // Auto-fill from profile
  const userProfile = UserProfileManager.get()

  // Game mode and phase
  const navigate = useNavigate()
  const [gameMode, setGameMode] = useState<GameMode>('select')
  const [phase, setPhase] = useState<GamePhase>('select')
  const [aiDifficulty, setAiDifficulty] = useState<AIDifficulty>('medium')

  // AI instance
  const aiInstanceRef = useRef<GoFishAI | null>(null)

  const [lobbyKey, setLobbyKey] = useState(0)

  // Local game state
  const [playerCount, setPlayerCount] = useState<2 | 3 | 4>(2)
  const [localPlayerNames, setLocalPlayerNames] = useState(['Player 1', 'Player 2', 'Player 3', 'Player 4'])
  const [players, setPlayers] = useState<Player[]>([])
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0)
  const [deck, setDeck] = useState<Card[]>([])
  const [winner, setWinner] = useState<Player | null>(null)
  const [askingRank, setAskingRank] = useState<Rank | null>(null)

  // Network game state
  const [playerName, setPlayerName] = useState(userProfile?.name || '')
  const [playerNumber, setPlayerNumber] = useState<1 | 2>(1)
  const [sessionCode, setSessionCode] = useState<string | undefined>(undefined)

  // Notification state
  const [notification, setNotification] = useState<Notification | null>(null)
  // Game log now uses historyItems via sidebar

  // Universal sidebar state
  const [chatMessages, setChatMessages] = useState<Message[]>([])
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([])
  const sidebar = useSidebar()

  const {
    session,
    loading,
    error,
    createSession,
    joinSession,
    sendMove,
    onMessage,
  } = useEnhancedGameSession({
    sessionCode,
    playerNumber,
    playerName,
  })

  // Auto-join if join code in URL — go to setup so user can enter name
  useEffect(() => {
    if (joinCode && phase === 'select') {
      setGameMode('network')
      setPhase('setup')
    }
  }, [joinCode, phase])

  // Auto-dismiss notification
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [notification])

  // Sync game state (network mode)
  useEffect(() => {
    if (gameMode !== 'network') return

    const unsubscribe = onMessage('game_state', (data) => {
      const state = data.state
      if (state.players) setPlayers(state.players)
      if (state.deck) setDeck(state.deck)
      if (state.currentPlayerIndex !== undefined) setCurrentPlayerIndex(state.currentPlayerIndex)
      if (state.gameLog) setHistoryItems(state.gameLog.map((msg: string) => ({ type: 'event' as const, content: msg, timestamp: Date.now() })))
      if (state.winner !== undefined) {
        setWinner(state.winner)
        if (state.winner) setPhase('finished')
      }
    })

    return () => unsubscribe()
  }, [gameMode, onMessage])

  const showNotification = (message: string, type: Notification['type'] = 'info') => {
    setNotification({ message, type })
  }

  const addLog = (message: string) => {
    setHistoryItems(prev => [...prev, { type: 'event', content: message, timestamp: Date.now() }])
  }

  const createDeck = (): Card[] => {
    const deck: Card[] = []
    for (const rank of RANKS) {
      for (const suit of SUITS) {
        deck.push({ rank, suit })
      }
    }
    return shuffleDeck(deck)
  }

  const shuffleDeck = (deck: Card[]): Card[] => {
    const shuffled = [...deck]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }

  const dealCards = (numPlayers: number): { players: Player[]; deck: Card[] } => {
    const newDeck = createDeck()
    const cardsPerPlayer = numPlayers === 2 ? 7 : 5
    const newPlayers: Player[] = []

    for (let i = 0; i < numPlayers; i++) {
      const hand = newDeck.splice(0, cardsPerPlayer)
      newPlayers.push({
        id: i,
        name: `Player ${i + 1}`,
        hand,
        books: []
      })
    }

    return { players: newPlayers, deck: newDeck }
  }

  const checkForBooks = (player: Player): { player: Player; newBooks: Rank[] } => {
    const rankCounts: { [key in Rank]?: number } = {}
    player.hand.forEach(card => {
      rankCounts[card.rank] = (rankCounts[card.rank] || 0) + 1
    })

    const newBooks: Rank[] = []
    Object.entries(rankCounts).forEach(([rank, count]) => {
      if (count === 4) {
        newBooks.push(rank as Rank)
        player.hand = player.hand.filter(card => card.rank !== rank)
        player.books.push(rank as Rank)
      }
    })

    return { player, newBooks }
  }

  const startLocalGame = (playerNames?: string[]) => {
    const { players: newPlayers, deck: newDeck } = dealCards(playerCount)

    // Update player names from provided array or state
    newPlayers.forEach((player, index) => {
      const name = playerNames?.[index]?.trim() || localPlayerNames[index]?.trim()
      if (name) player.name = name
    })

    // Check for initial books
    const initialLog: string[] = []
    newPlayers.forEach(player => {
      const result = checkForBooks(player)
      if (result.newBooks.length > 0) {
        const msg = `${player.name} starts with ${result.newBooks.length} book(s)!`
        initialLog.push(msg)
      }
    })
    initialLog.push('Game started!')

    setPlayers(newPlayers)
    setDeck(newDeck)
    setCurrentPlayerIndex(0)
    setPhase('playing')
    setHistoryItems(initialLog.map(msg => ({ type: 'event' as const, content: msg, timestamp: Date.now() })))

    // Send initial state if network mode
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        players: newPlayers,
        deck: newDeck,
        currentPlayerIndex: 0,
        gameLog: initialLog,
      }})
    }
  }

  const askForCard = (targetPlayerIndex: number, rank: Rank) => {
    const currentPlayer = players[currentPlayerIndex]
    const targetPlayer = players[targetPlayerIndex]

    // Check if current player has the rank
    if (!currentPlayer.hand.some(card => card.rank === rank)) {
      showNotification("You don't have that rank in your hand!", 'error')
      return
    }

    // Find matching cards
    const matchingCards = targetPlayer.hand.filter(card => card.rank === rank)

    if (matchingCards.length > 0) {
      // Transfer cards
      const newPlayers = [...players]
      newPlayers[targetPlayerIndex].hand = targetPlayer.hand.filter(card => card.rank !== rank)
      newPlayers[currentPlayerIndex].hand.push(...matchingCards)

      // Check for books
      const bookResult = checkForBooks(newPlayers[currentPlayerIndex])
      if (bookResult.newBooks.length > 0) {
        addLog(`${currentPlayer.name} completed ${bookResult.newBooks.length} book(s): ${bookResult.newBooks.join(', ')}!`)
        showNotification(`You made a book of ${rank}s!`, 'success')
      }

      // Refill target's empty hand from deck (Go Fish rules)
      let currentDeck = deck
      if (newPlayers[targetPlayerIndex].hand.length === 0 && currentDeck.length > 0) {
        newPlayers[targetPlayerIndex].hand.push(currentDeck[0])
        currentDeck = currentDeck.slice(1)
        addLog(`${targetPlayer.name} drew a card (empty hand)`)
        setDeck(currentDeck)
      }

      setPlayers(newPlayers)
      const logMsg = `${currentPlayer.name} got ${matchingCards.length} ${rank}(s) from ${targetPlayer.name}`
      addLog(logMsg)

      // Send state update
      if (gameMode === 'network') {
        sendMove({ type: 'game_state', state: {
          players: newPlayers,
          deck: currentDeck,
          currentPlayerIndex,
          gameLog: [...historyItems.map(h => h.content), logMsg],
        }})
      }

      // Same player goes again
      setAskingRank(null)
    } else {
      // Go fish!
      const logMsg = `${targetPlayer.name} said "Go Fish!"`
      addLog(logMsg)
      goFish()
    }

    checkWinCondition()
  }

  const goFish = () => {
    if (deck.length === 0) {
      nextTurn()
      return
    }

    const drawnCard = deck[0]
    const newDeck = deck.slice(1)
    const newPlayers = [...players]
    newPlayers[currentPlayerIndex].hand.push(drawnCard)

    addLog(`${players[currentPlayerIndex].name} drew a card`)

    // Check for books
    const bookResult = checkForBooks(newPlayers[currentPlayerIndex])
    if (bookResult.newBooks.length > 0) {
      addLog(`${players[currentPlayerIndex].name} completed ${bookResult.newBooks.length} book(s): ${bookResult.newBooks.join(', ')}!`)
      showNotification(`You made a book!`, 'success')
    }

    setPlayers(newPlayers)
    setDeck(newDeck)

    // Send state update
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        players: newPlayers,
        deck: newDeck,
        currentPlayerIndex,
        gameLog: historyItems.map(h => h.content),
      }})
    }

    // If the drawn card matches the asked rank, player goes again
    // Check BEFORE clearing askingRank
    if (askingRank && drawnCard.rank === askingRank) {
      showNotification('Lucky! You drew the card you asked for. Go again!', 'success')
      setAskingRank(null)
      // Don't call nextTurn() - player goes again
    } else {
      setAskingRank(null)
      nextTurn()
    }
  }

  const nextTurn = () => {
    let nextIndex = (currentPlayerIndex + 1) % players.length

    // Skip players with no cards
    let attempts = 0
    while (players[nextIndex].hand.length === 0 && attempts < players.length) {
      nextIndex = (nextIndex + 1) % players.length
      attempts++
    }

    setCurrentPlayerIndex(nextIndex)
    setAskingRank(null)

    // Send state update
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        players,
        deck,
        currentPlayerIndex: nextIndex,
        gameLog: historyItems.map(h => h.content),
      }})
    }
  }

  const checkWinCondition = () => {
    // Game ends when all books are made (13 books total)
    const totalBooks = players.reduce((sum, player) => sum + player.books.length, 0)

    if (totalBooks === 13 || deck.length === 0 && players.every(p => p.hand.length === 0)) {
      const winner = players.reduce((max, player) =>
        player.books.length > max.books.length ? player : max
      )
      setWinner(winner)
      setPhase('finished')
      showNotification(`${winner.name} wins with ${winner.books.length} books!`, 'success')

      // Send final state update
      if (gameMode === 'network') {
        sendMove({ type: 'game_state', state: {
          players,
          deck,
          currentPlayerIndex,
          gameLog: historyItems.map(h => h.content),
          winner,
        }})
      }
    }
  }

  // AI turn logic
  useEffect(() => {
    if (gameMode !== 'ai') return
    if (phase !== 'playing') return
    if (currentPlayerIndex !== 1) return // AI is player 2 (index 1)
    if (!aiInstanceRef.current) return
    if (players.length < 2) return

    const aiPlayer = players[1]
    if (aiPlayer.hand.length === 0) {
      // AI has no cards, skip turn
      nextTurn()
      return
    }

    // Update AI's hand
    aiInstanceRef.current.setHand(aiPlayer.hand)

    // AI makes a decision after a delay
    const delay = aiDifficulty === 'easy' ? 1500 : aiDifficulty === 'medium' ? 1200 : 800
    const timer = setTimeout(() => {
      if (!aiInstanceRef.current) return

      const rankToAsk = aiInstanceRef.current.chooseRankToAsk()
      const humanPlayer = players[0]

      addLog(`${aiPlayer.name} asks for ${rankToAsk}s`)
      showNotification(`AI asks: Do you have any ${rankToAsk}s?`, 'info')

      // Check if human has the rank
      const matchingCards = humanPlayer.hand.filter(card => card.rank === rankToAsk)

      if (matchingCards.length > 0) {
        // Human has the cards - transfer them
        const newPlayers = [...players]
        newPlayers[0].hand = humanPlayer.hand.filter(card => card.rank !== rankToAsk)
        newPlayers[1].hand.push(...matchingCards)

        // AI records successful ask
        aiInstanceRef.current.recordOpponentResponse(rankToAsk, true, matchingCards.length)

        // Check for books
        const bookResult = checkForBooks(newPlayers[1])
        if (bookResult.newBooks.length > 0) {
          addLog(`${aiPlayer.name} completed ${bookResult.newBooks.length} book(s): ${bookResult.newBooks.join(', ')}!`)
          bookResult.newBooks.forEach(rank => {
            aiInstanceRef.current?.recordOpponentBook(rank)
          })
        }

        // Refill human's empty hand from deck (Go Fish rules)
        let currentDeck = deck
        if (newPlayers[0].hand.length === 0 && currentDeck.length > 0) {
          newPlayers[0].hand.push(currentDeck[0])
          currentDeck = currentDeck.slice(1)
          addLog(`${humanPlayer.name} drew a card (empty hand)`)
          setDeck(currentDeck)
        }

        setPlayers(newPlayers)
        addLog(`${aiPlayer.name} got ${matchingCards.length} ${rankToAsk}(s) from ${humanPlayer.name}`)
        checkWinCondition()
        // AI goes again - don't call nextTurn()
      } else {
        // Human doesn't have it - Go Fish!
        aiInstanceRef.current.recordOpponentResponse(rankToAsk, false)
        addLog(`${humanPlayer.name} said "Go Fish!"`)

        if (deck.length === 0) {
          nextTurn()
          return
        }

        const drawnCard = deck[0]
        const newDeck = deck.slice(1)
        const newPlayers = [...players]
        newPlayers[1].hand.push(drawnCard)

        addLog(`${aiPlayer.name} drew a card`)

        // Check for books
        const bookResult = checkForBooks(newPlayers[1])
        if (bookResult.newBooks.length > 0) {
          addLog(`${aiPlayer.name} completed ${bookResult.newBooks.length} book(s): ${bookResult.newBooks.join(', ')}!`)
          bookResult.newBooks.forEach(rank => {
            aiInstanceRef.current?.recordOpponentBook(rank)
          })
        }

        setPlayers(newPlayers)
        setDeck(newDeck)

        checkWinCondition()

        // If drew the asked card, AI goes again
        if (drawnCard.rank === rankToAsk) {
          addLog(`${aiPlayer.name} drew the requested ${rankToAsk}!`)
          // AI goes again - don't call nextTurn()
        } else {
          nextTurn()
        }
      }
    }, delay)

    return () => clearTimeout(timer)
  }, [gameMode, phase, currentPlayerIndex, players, deck, aiDifficulty])

  // Record opponent asks when human asks AI (for AI learning)
  const askForCardWithAI = (targetPlayerIndex: number, rank: Rank) => {
    const currentPlayer = players[currentPlayerIndex]
    const targetPlayer = players[targetPlayerIndex]

    // Check if current player has the rank
    if (!currentPlayer.hand.some(card => card.rank === rank)) {
      showNotification("You don't have that rank in your hand!", 'error')
      return
    }

    // If human is asking AI, record it
    if (gameMode === 'ai' && targetPlayerIndex === 1 && aiInstanceRef.current) {
      const matchingCards = targetPlayer.hand.filter(card => card.rank === rank)
      aiInstanceRef.current.recordOpponentAsk(rank, matchingCards.length > 0)
    }

    // Continue with normal logic
    askForCard(targetPlayerIndex, rank)
  }

  // Handler for sidebar message sending
  const handleSidebarMessage = useCallback(async (message: string) => {
    if (gameMode === 'network' && sessionCode) {
      // Network mode - send via session
      await sendMove({ type: 'chat', message })

      // Add to local chat messages
      const newMessage: Message = {
        sender: 'you',
        content: message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    }
  }, [gameMode, sessionCode, sendMove])

  // Watch for player 2 joining (player 1 is in lobby phase waiting)
  useEffect(() => {
    if (session && phase === 'lobby' && session.player2_name) {
      setPhase('playing')
      showNotification(`${session.player2_name} joined!`, 'success')

      // Player 1 initializes the game
      if (playerNumber === 1) {
        const { players: newPlayers, deck: newDeck } = dealCards(2)
        newPlayers[0].name = session.player1_name
        newPlayers[1].name = session.player2_name

        // Check for initial books
        const initialLog: string[] = []
        newPlayers.forEach(player => {
          const result = checkForBooks(player)
          if (result.newBooks.length > 0) {
            const msg = `${player.name} starts with ${result.newBooks.length} book(s)!`
            initialLog.push(msg)
          }
        })
        initialLog.push('Game started!')

        setPlayers(newPlayers)
        setDeck(newDeck)
        setCurrentPlayerIndex(0)
        setHistoryItems(initialLog.map(msg => ({ type: 'event' as const, content: msg, timestamp: Date.now() })))

        // Broadcast initial state to player 2
        sendMove({ type: 'game_state', state: {
          players: newPlayers,
          deck: newDeck,
          currentPlayerIndex: 0,
          gameLog: initialLog,
        }})
      }
    }
  }, [session, phase, playerNumber, sendMove])

  // Handle real-time WebRTC messages for chat
  useEffect(() => {
    if (gameMode !== 'network') return

    const unsubscribeChat = onMessage('chat', (data) => {
      const newMessage: Message = {
        sender: data.playerNumber === playerNumber ? 'you' : 'opponent',
        content: data.message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    })

    return () => {
      unsubscribeChat()
    }
  }, [gameMode, onMessage, playerNumber])

  const NotificationComponent = () => {
    if (!notification) return null

    return (
      <div className={`notification notification-${notification.type}`}>
        {notification.message}
        <button className="notification-close" onClick={() => setNotification(null)}>×</button>
      </div>
    )
  }

  const getRankCards = (hand: Card[]): Map<Rank, Card[]> => {
    const rankMap = new Map<Rank, Card[]>()
    hand.forEach(card => {
      if (!rankMap.has(card.rank)) {
        rankMap.set(card.rank, [])
      }
      rankMap.get(card.rank)!.push(card)
    })
    return rankMap
  }

  // Pre-game lobby (select / setup / waiting for player 2)
  if (phase !== 'playing' && phase !== 'finished') {
    return (
      <GameLobby
        key={lobbyKey}
        gameTitle="Go Fish"
        gameIcon="🎣"
        gameDescription="Collect the most books of 4 matching cards"
        gameBanner="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
        supportedModes={['local', 'ai', 'network']}
        initialJoinCode={joinCode || ''}
        networkLoading={loading}
        networkError={error}
        sessionCode={phase === 'lobby' ? session?.session_code : undefined}
        onBack={() => navigate('/')}
        onStartLocal={(p1Name) => {
          const allNames = [p1Name, ...localPlayerNames.slice(1, playerCount)]
          setGameMode('local')
          startLocalGame(allNames)
        }}
        onStartAI={(name, diff) => {
          setPlayerName(name)
          setGameMode('ai')
          setAiDifficulty(diff)
          aiInstanceRef.current = new GoFishAI()
          const { players: newPlayers, deck: newDeck } = dealCards(2)
          newPlayers[0].name = name
          newPlayers[1].name = `AI (${diff})`
          const initialLog: string[] = []
          newPlayers.forEach(p => {
            const result = checkForBooks(p)
            if (result.newBooks.length > 0) initialLog.push(`${p.name} starts with ${result.newBooks.length} book(s)!`)
          })
          initialLog.push('Game started!')
          aiInstanceRef.current.setHand(newPlayers[1].hand)
          setPlayers(newPlayers)
          setDeck(newDeck)
          setCurrentPlayerIndex(0)
          setPhase('playing')
          setHistoryItems(initialLog.map(msg => ({ type: 'event' as const, content: msg, timestamp: Date.now() })))
        }}
        onCreateNetwork={async (name) => {
          setPlayerName(name)
          setGameMode('network')
          setPlayerNumber(1)
          const newSession = await createSession('gofish', name)
          setSessionCode(newSession.session_code)
          setPhase('lobby')
        }}
        onJoinNetwork={async (name, code) => {
          setPlayerName(name)
          setGameMode('network')
          setPlayerNumber(2)
          await joinSession(code, name)
          setSessionCode(code)
          setPhase('playing')
        }}
        onCancelWaiting={() => {
          setLobbyKey(k => k + 1)
          setPhase('select')
        }}
        extraSetupContent={(mode) => {
          if (mode !== 'local') return null
          return (
            <>
              <div className="gl-field">
                <label className="gl-label">Number of Players</label>
                <div className="gl-diff-row">
                  {([2, 3, 4] as const).map(n => (
                    <button
                      key={n}
                      className={`gl-diff-btn${playerCount === n ? ' active' : ''}`}
                      onClick={() => setPlayerCount(n)}
                    >
                      <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>{n}</span>
                      <span>Players</span>
                    </button>
                  ))}
                </div>
              </div>
              {playerCount > 1 && (
                <div className="gl-field">
                  <label className="gl-label">Other Player Names</label>
                  {Array.from({ length: playerCount - 1 }, (_, i) => (
                    <input
                      key={i + 1}
                      className="gl-input"
                      style={{ marginBottom: i < playerCount - 2 ? 8 : 0 }}
                      type="text"
                      placeholder={`Player ${i + 2}`}
                      value={localPlayerNames[i + 1]}
                      onChange={e => {
                        const names = [...localPlayerNames]
                        names[i + 1] = e.target.value
                        setLocalPlayerNames(names)
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          )
        }}
      />
    )
  }

  // Playing
  if (phase === 'playing') {
    // Player 2 joined but hasn't received game state yet
    if (players.length === 0) {
      return (
        <div className="gofish-container">
          <GameRoomHeader
            gameTitle="Go Fish"
            gameIcon="🎣"
            mode="network"
            players={[]}
            onBack={() => setPhase('setup')}
          />
          <div className="arcade-lobby-screen">
            <div className="arcade-lobby-content">
              <div className="arcade-lobby-banner">
                <h2>Starting game...</h2>
                <p style={{ color: 'var(--text-secondary)' }}>Waiting for host to deal cards</p>
              </div>
            </div>
          </div>
        </div>
      )
    }

    const currentPlayer = players[currentPlayerIndex]

    // In network mode, determine if it's this player's turn
    const isMyTurn = gameMode === 'network'
      ? currentPlayerIndex === (playerNumber - 1)
      : true // Local/AI mode always shows current player controls

    // In network mode, show own player's hand, not current player's
    const myPlayerIndex = gameMode === 'network' ? (playerNumber - 1) : currentPlayerIndex
    const myPlayer = players[myPlayerIndex] || currentPlayer
    const rankMap = getRankCards(myPlayer.hand)
    const availableRanks = Array.from(rankMap.keys())

    return (
      <div className="gofish-container universal-game-room">
          <NotificationComponent />

          {/* Universal Header */}
          <GameRoomHeader
            gameTitle="Go Fish"
            gameIcon="🎣"
            mode={gameMode as 'ai' | 'network' | 'local'}
            players={players.map((player, idx) => ({
              name: player.name,
              status: idx === currentPlayerIndex ? 'active' : undefined
            }))}
            sessionCode={session?.session_code}
            onBack={() => window.location.href = '/'}
            compact={true}
          />

          <div className="game-room-content">
            {/* Main game board */}
            <div className="game-board-area">
              <div className="game-board">
                <div className="game-status">
                  <div className="deck-info">
                    <div className="deck-pile">
                      <span className="deck-count">{deck.length}</span>
                      <span>cards left</span>
                    </div>
                  </div>

                  <div className="current-turn">
                    <span className="turn-label">Current Turn:</span>
                    <span className="player-name">{currentPlayer.name}</span>
                    {gameMode === 'network' && (
                      <span style={{ marginLeft: '8px', fontSize: '0.9em', opacity: 0.8 }}>
                        {isMyTurn ? '(Your turn)' : '(Waiting...)'}
                      </span>
                    )}
                  </div>

                </div>

                <div className="players-area">
                  {players.map((player, index) => {
                    // In network mode, show special styling for "you"
                    const isMe = gameMode === 'network' && index === myPlayerIndex
                    // AI mode: human is always index 0 — never reveal AI hand even briefly
                    const showHand = gameMode === 'network' ? isMe : gameMode === 'ai' ? index === 0 : index === currentPlayerIndex
                    const showControls = gameMode === 'network' ? (isMe && isMyTurn) : gameMode === 'ai' ? (index === 0 && currentPlayerIndex === 0) : index === currentPlayerIndex

                    return (
                      <div
                        key={player.id}
                        className={`player-panel ${index === currentPlayerIndex ? 'active' : ''} ${isMe ? 'my-player' : ''}`}
                      >
                        <div className="player-header">
                          <h3>
                            {player.name}
                            {isMe && <span style={{ marginLeft: '8px', fontSize: '0.8em', opacity: 0.7 }}>(You)</span>}
                          </h3>
                          <div className="player-stats">
                            <span className="hand-count">{player.hand.length} cards</span>
                            <span className="books-count">{player.books.length} books</span>
                          </div>
                        </div>

                        {player.books.length > 0 && (
                          <div className="books">
                            <AnimatePresence>
                              {player.books.map((rank, i) => (
                                <motion.div
                                  key={rank}
                                  className="book"
                                  initial={{ scale: 0, rotateY: 90 }}
                                  animate={{ scale: 1, rotateY: 0 }}
                                  exit={{ scale: 0, opacity: 0 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 260,
                                    damping: 20,
                                    delay: i * 0.1
                                  }}
                                >
                                  Book of {rank}s
                                </motion.div>
                              ))}
                            </AnimatePresence>
                          </div>
                        )}

                        {!showHand && player.hand.length > 0 && (
                          <div className="ai-hand-area">
                            {Array.from({ length: Math.min(player.hand.length, 7) }).map((_, i) => {
                              const BackCard = (PlayingCardDeck as Record<string, React.ComponentType<React.SVGProps<SVGElement>>>)['B1']
                              return (
                                <div key={i} className="ai-card-back">
                                  <BackCard style={{ width: '100%', height: '100%' }} />
                                </div>
                              )
                            })}
                            {player.hand.length > 7 && (
                              <span className="ai-card-overflow">+{player.hand.length - 7}</span>
                            )}
                          </div>
                        )}

                        {showHand && (
                          <div className="player-hand">
                            <AnimatePresence>
                              {Array.from(getRankCards(player.hand).entries()).map(([rank, cards], groupIndex) => (
                                <motion.div
                                  key={rank}
                                  className="card-group"
                                  initial={{ opacity: 0, y: 50, rotateY: -90 }}
                                  animate={{ opacity: 1, y: 0, rotateY: 0 }}
                                  exit={{ opacity: 0, scale: 0.8, rotateY: 90 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 200,
                                    damping: 25,
                                    delay: groupIndex * 0.05
                                  }}
                                  whileHover={{
                                    scale: 1.05,
                                    y: -10,
                                    transition: { type: "spring", stiffness: 400, damping: 10 }
                                  }}
                                >
                                  <div className="card-svg-wrapper">
                                    {(() => {
                                      const suitMap: Record<string, string> = { '♠': 'S', '♥': 'H', '♦': 'D', '♣': 'C' }
                                      const rankMap: Record<string, string> = { 'A': 'a', '2': '2', '3': '3', '4': '4', '5': '5', '6': '6', '7': '7', '8': '8', '9': '9', '10': '10', 'J': 'j', 'Q': 'q', 'K': 'k' }
                                      const firstCard = cards[0]
                                      const key = (suitMap[firstCard.suit] || 'S') + (rankMap[rank] || 'a')
                                      const CardSvg = (PlayingCardDeck as Record<string, React.ComponentType<React.SVGProps<SVGElement>>>)[key]
                                      return CardSvg ? <CardSvg style={{ width: '100%', height: '100%' }} /> : <div style={{background:'white',width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'20px',fontWeight:'bold',color:'#333'}}>{rank}</div>
                                    })()}
                                    {cards.length > 1 && <div className="card-count-badge">×{cards.length}</div>}
                                  </div>
                                </motion.div>
                              ))}
                            </AnimatePresence>
                          </div>
                        )}

                        {showControls && !askingRank && (
                          <motion.div
                            className="ask-controls"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                          >
                            <p>Select a rank to ask for:</p>
                            <div className="rank-buttons">
                              {availableRanks.map((rank, i) => (
                                <motion.button
                                  key={rank}
                                  onClick={() => setAskingRank(rank)}
                                  className="rank-button"
                                  initial={{ opacity: 0, scale: 0 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.95 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 260,
                                    damping: 20,
                                    delay: i * 0.03
                                  }}
                                >
                                  {rank}
                                </motion.button>
                              ))}
                            </div>
                          </motion.div>
                        )}

                        {askingRank && showControls && (
                          <motion.div
                            className="target-selection"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.2 }}
                          >
                            <p>Ask for <strong>{askingRank}s</strong> from:</p>
                            <div className="target-buttons">
                              {players.filter((_, i) => i !== myPlayerIndex).map((targetPlayer, i) => (
                                <motion.button
                                  key={targetPlayer.id}
                                  onClick={() => {
                                    const targetIndex = players.indexOf(targetPlayer)
                                    if (gameMode === 'ai') {
                                      askForCardWithAI(targetIndex, askingRank)
                                    } else {
                                      askForCard(targetIndex, askingRank)
                                    }
                                  }}
                                  className="target-button"
                                  disabled={targetPlayer.hand.length === 0 && deck.length === 0}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  whileHover={{ scale: 1.05, y: -2 }}
                                  whileTap={{ scale: 0.95 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 20,
                                    delay: i * 0.05
                                  }}
                                >
                                  {targetPlayer.name}
                                </motion.button>
                              ))}
                              <motion.button
                                onClick={() => setAskingRank(null)}
                                className="cancel-button"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                transition={{
                                  delay: players.filter((_, i) => i !== myPlayerIndex).length * 0.05 + 0.05
                                }}
                              >
                                Cancel
                              </motion.button>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Universal Sidebar - all modes */}
            <GameRoomSidebar
              mode={gameMode as 'local' | 'ai' | 'network'}
              gameState={{
                turn: `${currentPlayer.name}'s Turn`,
                score: Object.fromEntries(players.map(p => [p.name, `${p.books.length} books`]))
              }}
              messages={chatMessages}
              historyItems={historyItems}
              onSendMessage={handleSidebarMessage}
              collapsed={sidebar.collapsed}
              onToggleCollapse={sidebar.toggle}
              position={sidebar.position}
              quickReactions={true}
            />
          </div>
      </div>
    )
  }

  // Game finished
  if (phase === 'finished' && winner) {
    return (
      <div className="gofish-container">
        <NotificationComponent />
        <GameRoomHeader
          gameTitle="Go Fish"
          gameIcon="🎣"
          mode={gameMode === 'ai' ? 'ai' : gameMode === 'network' ? 'network' : 'local'}
          players={players.map(p => ({ name: p.name, avatar: '🎣', status: 'active' as const }))}
          sessionCode={gameMode === 'network' ? session?.session_code : undefined}
          onBack={() => navigate('/')}
        />
        <div className="gofish-winner-card">
          <div className="gofish-winner-banner">
            <div style={{ fontSize: '4rem', marginBottom: '12px' }}>🎉</div>
            <h2 style={{ color: 'white', fontSize: 'var(--font-size-3xl)', margin: '0 0 8px' }}>{winner.name} wins!</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>with {winner.books.length} books</p>
            <div style={{ marginBottom: '24px' }}>
              {[...players]
                .sort((a, b) => b.books.length - a.books.length)
                .map((player, index) => (
                  <div key={player.id} className={`gofish-score-row ${index === 0 ? 'first' : 'other'}`}>
                    <span>{index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}</span>
                    <span style={{ fontWeight: 600, color: 'white' }}>{player.name}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{player.books.length} books</span>
                  </div>
                ))}
            </div>
            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%' }}
              onClick={() => {
                setPhase('select')
                setGameMode('select')
                setHistoryItems([])
                setWinner(null)
                setPlayers([])
              }}
            >
              Play Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Fallback
  return null
}
